import {
  createSelector,
} from 'reselect';

import type {
  Resources,
  ResourceType,
} from '../types';

import {
  getResourceById,
  getResourceMappedList,
  getSelectionResourceMappedList,
} from './resources';
import {
  alphSortByName,
  alphSortByUserName,
  alphSortSearchOptions,
} from './utils';

const groupItemsSelectors: { [key: string]: any } = {};
export const getGroupItems = (
  resourceType: ResourceType,
  groupId: number,
): (state: Resources) => any[] => {
  const selectorName = `${resourceType}_${groupId}`;
  if (groupItemsSelectors[selectorName]) {
    return groupItemsSelectors[selectorName];
  }
  groupItemsSelectors[selectorName] = createSelector(
    [
      getResourceById('clientGroups', groupId),
      getSelectionResourceMappedList('clients'),
      getSelectionResourceMappedList('programTemplates'),
      getSelectionResourceMappedList('workoutTemplates'),
      getSelectionResourceMappedList('documents'),
    ],
    (
      groupInfo,
      clients,
      programs,
      workouts,
      documents,
    ) => {
      switch (resourceType) {
        case 'clients':
          return clients
            .filter((client: any) => groupInfo.clients?.includes(client.id))
            .sort(alphSortByUserName);
        case 'programTemplates':
          return programs
            .filter((program: any) => groupInfo.programTemplates?.includes(program.id))
            .sort(alphSortByName);
        case 'workoutTemplates':
          return workouts
            .filter((workout: any) => groupInfo.workoutTemplates?.includes(workout.id))
            .sort(alphSortByName);
        case 'documents':
          return documents
            .filter((document: any) => groupInfo.documents?.includes(document.id))
            .sort(alphSortByName);
        default:
          return [];
      }
    },
  );

  return groupItemsSelectors[selectorName];
};

const groupOptionsSelectors = {};
export const getSearchGroupOptions = (groupId: number, resourceType: ResourceType, list = 'all') => {
  const selectorName = `${resourceType}_${groupId}`;
  if (groupOptionsSelectors[selectorName]) {
    return groupOptionsSelectors[selectorName];
  }
  groupOptionsSelectors[selectorName] = createSelector(
    [
      getResourceMappedList(resourceType, list),
      getResourceById('clientGroups', groupId),
    ],
    (
      items,
      groupInfo,
    ) => {
      switch (resourceType) {
        case 'clients':
          return items
            .filter(
              client => groupInfo?.clients?.indexOf(client.id) === -1,
            )
            .map(
              option => ({
                label: `${option.user_profile.first_name} ${option.user_profile.last_name}`,
                value: String(option.id),
                color: option.color,
                avatar: option.user_profile.avatar?.thumb?.url,
              }),
            )
            .sort(alphSortSearchOptions);
        case 'workoutTemplates':
          return items
            .filter(op => op.is_visible)
            .map(
              option => ({
                label: option.name,
                value: String(option.id),
              }),
            )
            .sort(alphSortSearchOptions);
        default:
          return items
            .map(
              option => ({
                label: option.name,
                value: String(option.id),
              }),
            )
            .sort(alphSortSearchOptions);
      }
    },
  );
  return groupOptionsSelectors[selectorName];
};

export const getGroupItemAttrs = (groupId: number, clientId: number) => createSelector(
  [
    getResourceById('clientGroups', groupId),
    getResourceById('clients', clientId),
  ],
  (group, client) => ({
    groupId,
    groupName: group.name,
    clientId,
    clientName: `${client.user_profile.first_name} ${client.user_profile.last_name}`,
  }),
);
